import {
  Box,
  Card,
  Divider,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { TypeColumn } from '../groupTable/TypeColumn';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import { useTranslation } from 'react-i18next';
import type { CashTransactionGroupType } from '@pflegenavi/shared/api';
import { FeatureFlag } from '@pflegenavi/shared/api';
import { formatTypeColumn } from '../groupTable/model';
import { TIME_FORMAT } from '@pflegenavi/shared/constants';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { Link } from '@pflegenavi/web-components';

interface GroupOverviewProps {
  type: CashTransactionGroupType;
  lastUpdateDate: Date;
  cashList?: { id: string; name: string };

  cashAmount: number;
  separateFeeAmount: number;
  immediateFeeAmount: number;

  linkedAmount: number;
  linkedSeparateFeeAmount: number;
  linkedImmediateFeeAmount: number;
  expensePartCount?: number;
  /** The group's cash transactions, shown at the bottom of the card. */
  children?: ReactNode;
}

export const GroupOverview: FC<GroupOverviewProps> = ({
  type,
  lastUpdateDate,
  cashList,
  linkedAmount,
  linkedSeparateFeeAmount,
  linkedImmediateFeeAmount,
  cashAmount,
  separateFeeAmount,
  immediateFeeAmount,
  expensePartCount = 0,
  children,
}) => {
  const { t } = useTranslation();
  const fDate = useFormatDate();
  const expenseManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ExpenseManagement
  );
  const cashListTransactionsPath = useFeatureFlagEnabled(
    FeatureFlag.AccountingPaymentsNew
  )
    ? nhAppMainPages.NURSING_HOME_ACCOUNTING_TRANSACTIONS
    : nhAppMainPages.CASH_MANAGEMENT_TRANSACTIONS;
  const row = useMemo(() => {
    return formatTypeColumn(
      { type, expensePartCount },
      { t, useGenericTypeNames: expenseManagementEnabled }
    );
  }, [t, type, expensePartCount, expenseManagementEnabled]);

  const difference = cashAmount - linkedAmount;
  const separateFeeAmountDifference =
    separateFeeAmount - linkedSeparateFeeAmount;
  const immediateFeeAmountDifference =
    immediateFeeAmount - linkedImmediateFeeAmount;

  const showSeparateFeeAmount =
    separateFeeAmount !== 0 || linkedSeparateFeeAmount !== 0;
  const showImmediateFeeAmount =
    immediateFeeAmount !== 0 || linkedImmediateFeeAmount !== 0;

  const smallScreens = useMediaQuery('(max-width:895px)');

  return (
    <Card>
      <Stack
        sx={{
          gap: 2,
          p: 3,
          bgcolor: 'background.neutral',
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            gap: 3,
          }}
        >
          <TypeColumn row={row} />
          <Stack sx={{ alignItems: 'flex-end', gap: 0.5 }}>
            {cashList && (
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                {t('cashManagement.group-detail.cash-list')}:{' '}
                <Link
                  to={`${cashListTransactionsPath}?cashListId=${cashList.id}`}
                  sx={{ fontSize: 14, fontWeight: 400 }}
                >
                  {cashList.name}
                </Link>
              </Typography>
            )}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              {t('cashManagement.group-detail.last-update')}{' '}
              <Typography
                sx={{
                  display: 'inline',
                  fontSize: 14,
                }}
              >
                {fDate(lastUpdateDate, TIME_FORMAT)}
              </Typography>
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Stack
          direction="row"
          sx={{
            justifyContent: smallScreens ? 'center' : 'space-between',
            flexWrap: 'wrap',
            gap: 3,
          }}
        >
          <HeaderAmountColumn
            label={t('cashManagement.group-detail.cash-amount')}
            amount={cashAmount}
            separateFeeAmount={separateFeeAmount}
            showSeparateFeeAmount={showSeparateFeeAmount}
            separateFeeAmountTooltipText={t(
              'cashManagement.group-detail.separate-fee-amount'
            )}
            immediateFeeAmount={immediateFeeAmount}
            showImmediateFeeAmount={showImmediateFeeAmount}
            immediateFeeAmountTooltipText={t(
              'cashManagement.group-detail.immediate-fee-amount'
            )}
          />
          <HeaderAmountColumn
            label={t('cashManagement.group-detail.linked-amount')}
            amount={linkedAmount}
            separateFeeAmount={linkedSeparateFeeAmount}
            showSeparateFeeAmount={showSeparateFeeAmount}
            separateFeeAmountTooltipText={t(
              'cashManagement.group-detail.linked-separate-fee-amount'
            )}
            immediateFeeAmount={linkedImmediateFeeAmount}
            showImmediateFeeAmount={showImmediateFeeAmount}
            immediateFeeAmountTooltipText={t(
              'cashManagement.group-detail.linked-immediate-fee-amount'
            )}
          />

          <HeaderAmountColumn
            label={t('cashManagement.group-detail.open-amount')}
            amount={difference}
            separateFeeAmount={separateFeeAmountDifference}
            showSeparateFeeAmount={showSeparateFeeAmount}
            separateFeeAmountTooltipText={t(
              'cashManagement.group-detail.open-separate-fee-amount'
            )}
            immediateFeeAmount={immediateFeeAmountDifference}
            showImmediateFeeAmount={showImmediateFeeAmount}
            immediateFeeAmountTooltipText={t(
              'cashManagement.group-detail.open-immediate-fee-amount'
            )}
          />
        </Stack>
      </Stack>
      {children}
    </Card>
  );
};

interface HeaderAmountColumnProps {
  label: string;
  amount: number;
  separateFeeAmount: number;
  showSeparateFeeAmount: boolean;
  separateFeeAmountTooltipText: string;
  immediateFeeAmount: number;
  showImmediateFeeAmount: boolean;
  immediateFeeAmountTooltipText: string;
}

const HeaderAmountColumn: FC<HeaderAmountColumnProps> = ({
  label,
  amount,
  separateFeeAmount,
  showSeparateFeeAmount,
  separateFeeAmountTooltipText,
  immediateFeeAmount,
  showImmediateFeeAmount,
  immediateFeeAmountTooltipText,
}) => {
  const { fCurrencyCents } = useFormatting();
  return (
    <Stack
      sx={{
        gap: 1,
      }}
    >
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
      <Stack
        direction="column"
        sx={{
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: 250,
            backgroundColor: 'grey.300',
            borderRadius: 2,
            p: 1,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
            }}
          >
            {fCurrencyCents(amount)}
          </Typography>
        </Box>
        {(showSeparateFeeAmount || showImmediateFeeAmount) && (
          <Stack
            direction="row"
            sx={{
              gap: 0.5,
            }}
          >
            {showImmediateFeeAmount && (
              <Tooltip
                arrow
                placement="left"
                title={immediateFeeAmountTooltipText}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                    }}
                  >
                    {fCurrencyCents(immediateFeeAmount)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
            {showSeparateFeeAmount && (
              <Tooltip
                arrow
                placement="right"
                title={separateFeeAmountTooltipText}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: 'warning.main',
                    }}
                  >
                    {fCurrencyCents(separateFeeAmount)}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
